import { ProjectFolderResponse, GetProjectResponse } from "api/generatedApi";
import { ExtendedProjectFolder } from "pages/project/hooks/use-map-tree-to-flat";
import { Role, useRoleRestriction } from "./use-role-restriction";

export const useFolderRestrictions = (project: GetProjectResponse) => {
  const { hasRole } = useRoleRestriction(project);

  const canCreateFolder = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  const canDeleteFolder = (hasSelectedFolder: ProjectFolderResponse): boolean => {
    if (!hasSelectedFolder) {
      return false;
    }
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  const canEditFolder = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  const canMoveFolder = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  const canCopyFolder = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  const canLockFolder = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  const canSeeFolderMenu = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager, Role.Participant]);
  };

  const canEditNote = () => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager]);
  };

  const canUploadeDocument = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager, Role.Participant]);
  };

  const canDeleteDocument = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  const canDownloadDocument = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Participant, Role.Manager]);
  };

  const canMultiplyFolder = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager, Role.Participant]);
  };

  const canMarkExtraWork = (folder: ExtendedProjectFolder): boolean => {
    if (!folder.parent || folder.isRoot) {
      return false;
    }
    if (folder.parent.folderExtraWork === "ExtraWork") {
      return false;
    }
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  const canCreateDraft = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Participant]);
  };
  const canDeleteDraft = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Participant]);
  };
  const canMoveDraftIntoFolder = (folder?: ExtendedProjectFolder): boolean => {
    if (hasRole([Role.Owner, Role.AssistantOwner])) {
      return true;
    }
    return folder?.projectFolderLocked === "Unlocked";
  };

  const canUpdateSupplementsOnFolder = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  return {
    canCreateFolder,
    canEditFolder,
    canDeleteFolder,
    canMoveFolder,
    canCopyFolder,
    canLockFolder,
    canSeeFolderMenu,
    canEditNote,
    canUploadeDocument,
    canDeleteDocument,
    canDownloadDocument,
    canMarkExtraWork,
    canCreateDraft,
    canDeleteDraft,
    canMoveDraftIntoFolder,
    canUpdateSupplementsOnFolder,
    canMultiplyFolder,
  };
};
